/**
 * MELP Mail Typography System
 *
 * Stack: Roboto → system UI fallbacks. Roboto is loaded explicitly via
 * <link> in each app's index.html so all six weights are guaranteed.
 *
 * Note: an earlier version of this file specified `"Google Sans"` first.
 * Google Sans is a proprietary font NOT served by the public fonts CDN —
 * the request 404'd silently and typography fell back to Roboto. To make
 * the actual rendered font explicit (and avoid future confusion), Google
 * Sans is removed; we now declare what we actually load.
 */

const HEADING_STACK =
  '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif';

const BODY_STACK = HEADING_STACK;

export const typography = {
  fontFamily: BODY_STACK,
  fontSize: 14,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 700,
  h1: {
    fontFamily: HEADING_STACK,
    fontWeight: 500,
    fontSize: '2.5rem',
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
  },
  h2: {
    fontFamily: HEADING_STACK,
    fontWeight: 500,
    fontSize: '2rem',
    lineHeight: 1.3,
    letterSpacing: '-0.00833em',
  },
  h3: {
    fontFamily: HEADING_STACK,
    fontWeight: 500,
    fontSize: '1.75rem',
    lineHeight: 1.4,
    letterSpacing: '0em',
  },
  h4: {
    fontFamily: HEADING_STACK,
    fontWeight: 500,
    fontSize: '1.5rem',
    lineHeight: 1.4,
    letterSpacing: '0.00735em',
  },
  h5: {
    fontFamily: HEADING_STACK,
    fontWeight: 500,
    fontSize: '1.25rem',
    lineHeight: 1.5,
    letterSpacing: '0em',
  },
  h6: {
    fontFamily: HEADING_STACK,
    fontWeight: 500,
    fontSize: '1.125rem',
    lineHeight: 1.6,
    letterSpacing: '0.0075em',
  },
  subtitle1: {
    fontFamily: HEADING_STACK,
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: 1.75,
    letterSpacing: '0.00938em',
  },
  subtitle2: {
    fontFamily: HEADING_STACK,
    fontWeight: 500,
    fontSize: '0.875rem',
    lineHeight: 1.57,
    letterSpacing: '0.00714em',
  },
  body1: {
    fontFamily: BODY_STACK,
    fontWeight: 400,
    fontSize: '1rem',
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  body2: {
    fontFamily: BODY_STACK,
    fontWeight: 400,
    fontSize: '0.875rem',
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
  },
  button: {
    fontFamily: HEADING_STACK,
    fontWeight: 500,
    fontSize: '0.875rem',
    lineHeight: 1.75,
    letterSpacing: '0.02857em',
    textTransform: 'none' as const,
  },
  caption: {
    fontFamily: BODY_STACK,
    fontWeight: 400,
    fontSize: '0.75rem',
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
  },
  overline: {
    fontFamily: BODY_STACK,
    fontWeight: 500,
    fontSize: '0.75rem',
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase' as const,
  },
} as const;

export type Typography = typeof typography;
